import React, { useState, useEffect } from "react";
import { Editor } from "@tiptap/react";
import { cn } from "@/utils/utils";
import Input from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";

/** Already-translated copy for the popover's fields and actions. */
export interface LinkPopoverLabels {
  enterLink: string;
  linkPlaceholder: string;
  displayText: string;
  displayTextPlaceholder: string;
  cancel: string;
  save: string;
}

interface LinkPopoverProps {
  editor: Editor | null;
  labels: LinkPopoverLabels;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

const LinkPopover: React.FC<LinkPopoverProps> = ({
  editor,
  labels,
  isOpen,
  onClose,
  className
}) => {
  const [linkUrl, setLinkUrl] = useState("");
  const [displayText, setDisplayText] = useState("");

  useEffect(() => {
    if (isOpen && editor) {
      const { from, to } = editor.state.selection;
      const selectedText = editor.state.doc.textBetween(from, to, "");

      // Check if we're editing an existing link
      if (editor.isActive("link")) {
        const linkAttrs = editor.getAttributes("link");
        setLinkUrl(linkAttrs.href || "");
        setDisplayText(selectedText || "");
      } else {
        // New link
        setLinkUrl("");
        setDisplayText(selectedText || "");
      }
    }
  }, [isOpen, editor]);

  const handleSave = () => {
    if (!editor || !linkUrl.trim()) return;

    const url =
      linkUrl.startsWith("http://") || linkUrl.startsWith("https://")
        ? linkUrl
        : `https://${linkUrl}`;

    if (displayText.trim()) {
      // If we have display text, insert/update the link with that text
      const { from, to } = editor.state.selection;
      editor
        .chain()
        .focus()
        .deleteRange({ from, to })
        .insertContent(`<a href="${url}">${displayText}</a>`)
        .run();
    } else {
      // If no display text, just set the link on selected text or insert URL as text
      editor.chain().focus().setLink({ href: url }).run();
    }

    onClose();
  };

  const handleCancel = () => {
    setLinkUrl("");
    setDisplayText("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        "absolute bottom-full left-0 mb-1 bg-surface-elevation1 border border-border-border1 rounded-md shadow-lg z-50 p-4 w-80",
        className
      )}
    >
      <div className="space-y-3">
        <div>
          <Text variant="bodySecondary" className="text-sm mb-1">
            {labels.enterLink}
          </Text>
          <Input
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder={labels.linkPlaceholder}
            className="w-full"
            autoFocus
          />
        </div>

        <div>
          <Text variant="bodySecondary" className="text-sm mb-1">
            {labels.displayText}
          </Text>
          <Input
            value={displayText}
            onChange={(e) => setDisplayText(e.target.value)}
            placeholder={labels.displayTextPlaceholder}
            className="w-full"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={handleCancel}>
            {labels.cancel}
          </Button>
          <Button onClick={handleSave} disabled={!linkUrl.trim()}>
            {labels.save}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LinkPopover;
